import ProtectedPageHeader from 'components/ProtectedPageHeader'
import TabItem from 'components/Tabs/TabItem'
import TabNav from 'components/Tabs/TabNav'
import TabPane from 'components/Tabs/TabPane'
import useFlag from 'hooks/useFlag'
import { useReportPermission } from 'hooks/useReportPermission'
import { buildTranslate } from 'locales'
import React, { useEffect, useState } from 'react'
import Tab from 'react-bootstrap-five/Tab'
import Helmet from 'react-helmet'
import { useAbility } from 'utilities/ability'
import AllCustomersTab from './AllCustomersTab'
import LoyaltyMembersTab from './LoyaltyMembersTab'
import SignupsByLocationTab from './SignupsByLocationTab'
import SignupsByUtmTab from './SignupsByUtmTab'

const t = buildTranslate('reports.customer')
enum Tabs {
  ALL_CUSTOMERS = 'all_customers',
  LOYALTY_MEMBERS = 'loyalty_members',
  SIGNUPS_BY_UTM = 'signups_by_utm',
  SIGNUPS_BY_LOCATION = 'signups_by_location',
}

const CustomerReport: React.FC = () => {
  const [activeTab, setActiveTab] = useState<Tabs>(Tabs.LOYALTY_MEMBERS)
  const pageTitle = t(`tabs.${activeTab}.title_tag`)
  const ability = useAbility()
  const rbacEnabled = useFlag('role-based-access-control', false)
  // LocationReports is a more specific RBAC subject than the page-level Reports guard.
  // The page gate uses Reports/Insights to control access to the entire page,
  // while this controls visibility of location-specific tabs within the page.
  const canAccessLocationReports = rbacEnabled
    ? ability.can('access', 'LocationReports')
    : ability.can('manage', 'Insights')
  const reportPermission = useReportPermission({
    rbac: 'Reports',
    legacy: 'Insights',
    legacyAction: 'manage',
  })

  useEffect(() => {
    if (
      !canAccessLocationReports &&
      (activeTab === Tabs.SIGNUPS_BY_UTM ||
        activeTab === Tabs.SIGNUPS_BY_LOCATION)
    ) {
      setActiveTab(Tabs.LOYALTY_MEMBERS)
    }
  }, [canAccessLocationReports, activeTab])

  return (
    <ProtectedPageHeader
      title={t('title')}
      resource={reportPermission.resource}
      action={reportPermission.action}
    >
      <Helmet>
        <title>{pageTitle}</title>
      </Helmet>
      <Tab.Container
        activeKey={activeTab}
        transition={false}
        onSelect={(tab: string | null) => {
          if (!tab) return
          setActiveTab(tab as Tabs)
        }}
      >
        <TabNav>
          <TabItem
            state={Tabs.LOYALTY_MEMBERS}
            title={t('tabs.loyalty_members.title')}
          />
          <TabItem
            state={Tabs.ALL_CUSTOMERS}
            title={
              <div className="d-flex align-content-center">
                <span className="mr-xxs">{t('tabs.all_customers.title')}</span>
              </div>
            }
          />
          {canAccessLocationReports && (
            <>
              <TabItem
                state={Tabs.SIGNUPS_BY_UTM}
                title={t('tabs.signups_by_utm.title')}
              />
              <TabItem
                state={Tabs.SIGNUPS_BY_LOCATION}
                title={t('tabs.signups_by_location.title')}
              />
            </>
          )}
        </TabNav>
        <Tab.Content className="pb-l h-100 grey-05-bkg">
          <TabPane state={Tabs.ALL_CUSTOMERS}>
            <AllCustomersTab />
          </TabPane>
          <TabPane state={Tabs.LOYALTY_MEMBERS}>
            <LoyaltyMembersTab />
          </TabPane>
          {canAccessLocationReports && (
            <>
              <TabPane state={Tabs.SIGNUPS_BY_UTM}>
                <SignupsByUtmTab />
              </TabPane>
              <TabPane state={Tabs.SIGNUPS_BY_LOCATION}>
                <SignupsByLocationTab />
              </TabPane>
            </>
          )}
        </Tab.Content>
      </Tab.Container>
    </ProtectedPageHeader>
  )
}

export default CustomerReport
